/**
 * Date Type Utilities (Weekend Detection, Day Display)
 *
 * ShelfCycle Standard: Org timezone should ALWAYS drive display, not browser local time.
 *
 * All functions that check day-of-week or display day names accept an optional timezone
 * parameter. Always pass the org timezone for correct business-day calculations.
 *
 * @example
 * // CORRECT - uses org timezone
 * isWeekend(date, timezone)
 *
 * // INCORRECT - uses browser timezone (may give wrong day)
 * isWeekend(date)
 *
 * See docs/TIMEZONE_STANDARDS.md for comprehensive timezone handling documentation.
 *
 * @module date
 */
import { DateTime } from "luxon";

type Dateish = Date | null | undefined;

export const warningColor = "#ff922b";
export const warningSymbol = String.fromCharCode(9888);

/**
 * Get the day of week (1-7, Monday=1, Sunday=7) for a date in a specific timezone.
 * Uses Luxon to properly handle timezone conversion.
 *
 * ShelfCycle Standard: Org timezone drives display, not browser local time.
 */
const getDayOfWeekInTimezone = (date: Date, timezone?: string): number => {
  const dt = timezone ? DateTime.fromJSDate(date, { zone: timezone }) : DateTime.fromJSDate(date);
  return dt.weekday; // 1 = Monday, 7 = Sunday
};

/**
 * Get the day name for display in a specific timezone.
 */
const getDayNameInTimezone = (date: Date, timezone?: string): string => {
  const dt = timezone ? DateTime.fromJSDate(date, { zone: timezone }) : DateTime.fromJSDate(date);
  return dt.weekdayLong ?? "";
};

export function weekendWarningDisplay(date: Dateish, timezone?: string) {
  return isWeekend(date, timezone) ? `${warningSymbol} ${dateDayDisplay(date, timezone)}` : "";
}

/**
 * Check if a date falls on a weekend (Saturday or Sunday) in the given timezone.
 *
 * @param date - The date to check
 * @param timezone - IANA timezone string (e.g., "America/Chicago"). If not provided,
 *                   uses local browser timezone (not recommended per ShelfCycle standard).
 */
export function isWeekend(date: Dateish, timezone?: string) {
  if (!date) {
    return false;
  }
  const weekday = getDayOfWeekInTimezone(date, timezone);
  // Luxon weekday: 6 = Saturday, 7 = Sunday
  return weekday === 6 || weekday === 7;
}

/**
 * Get the display name of the day of week in the given timezone.
 *
 * @param date - The date to display
 * @param timezone - IANA timezone string. If not provided, uses local browser timezone.
 */
export function dateDayDisplay(date: Dateish, timezone?: string) {
  if (!date) {
    return "";
  }
  return getDayNameInTimezone(date, timezone);
}

const renderDateFormat = "M-d-yyyy";
export const renderDate = (input: Date) => DateTime.fromJSDate(input).toFormat(renderDateFormat);
// Org-timezone-aware variant: every user sees the same calendar day regardless of browser timezone.
export const renderDateInTimezone = (input: Date, timezone: string) =>
  DateTime.fromJSDate(input, { zone: timezone }).toFormat(renderDateFormat);

const renderTimeFormat = "hh:mm:ss a";
export const renderTime = (input: Date) => DateTime.fromJSDate(input).toFormat(renderTimeFormat);
